import { QRCode } from 'antd';
import Modal from 'components/Modal';
import {
  TypePaymentActors,
  TypePaymentPassData,
} from 'features/payment/pages/PaymentPage/services/types/socketPayment';
import paymentApis from 'features/payment/services/apis';
import { IStaffInfoResItem } from 'features/payment/services/types/api';
import { useSocketContext } from 'hooks/useSocket';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { IResponseDataBody } from 'services/response';
import { uiLoadingBasicRef } from 'services/UI/LoadingPage/UILoadingRef';
import styled from 'styled-components';
import { PAYMENT_PASS_DATA_TOPIC } from 'utils/socket';
import scanQrIcon from './scan-qr.svg';
import shopSelectors from 'services/shop/selectors';

export type TipToStaffRef = {
  /**
   * Fetch staff info for a bill, broadcast `REQUEST_TIP_TO_STAFF` and show the
   * tip screen. Returns a promise that stays pending until `close()` is called
   * — payment flows call it from their `listeningData` once `DONE_TIP_TO_STAFF`
   * arrives — so callers can `await open()` before moving on.
   */
  open: (billId: string) => Promise<void>;
  /** Close the tip screen and resolve the pending `open()` promise. */
  close: () => void;
};

export const useTipToStaffRef = () => useRef<TipToStaffRef>(null);

/** Size of the QR canvas in px (matches the 275px Figma spec). */
const QR_SIZE = 276;

const TipToStaff = forwardRef<TipToStaffRef>((_props, ref) => {
  const [open, setOpen] = useState(false);
  const [staffs, setStaffs] = useState<IStaffInfoResItem[]>([]);
  const resolveRef = useRef<(() => void) | null>(null);
  const billIdRef = useRef('');
  const allSetting = shopSelectors.data.allSetting();

  const socketContext = useSocketContext();

  /** Close the screen and let any awaiting caller continue its flow. */
  const finish = () => {
    setOpen(false);
    const resolve = resolveRef.current;
    resolveRef.current = null;
    resolve?.();
  };

  const getListStaffInfo = async (billId: string): Promise<IStaffInfoResItem[]> => {
    uiLoadingBasicRef.current?.setVisible(true);
    try {
      const res: IResponseDataBody<IStaffInfoResItem[]> =
        await paymentApis.getListStaffInfo(billId);
      return res.data.data || [];
    } catch (error) {
      return [];
    } finally {
      uiLoadingBasicRef.current?.setVisible(false);
    }
  };

  useImperativeHandle(ref, () => ({
    open: async (billId: string) => {
      // Resolve a previous still-pending session before starting a new one.
      if (resolveRef.current) {
        const prev = resolveRef.current;
        resolveRef.current = null;
        prev();
      }
      if (!billId) return;

      if (!allSetting?.displayQrCodeTipStaff) return;

      const list = await getListStaffInfo(billId);
      // No staff to tip — resolve immediately so the flow keeps going.
      if (!list.length) return;

      billIdRef.current = billId;
      setStaffs(list);
      setOpen(true);
      // Tell the customer side to show its tip screen for this bill.
      socketContext.send(PAYMENT_PASS_DATA_TOPIC, {
        billId,
        actor: TypePaymentActors.CASHIER,
        action: TypePaymentPassData.REQUEST_TIP_TO_STAFF,
        data: { billId },
      });

      return new Promise<void>((resolve) => {
        resolveRef.current = resolve;
      });
    },
    close: () => finish(),
  }));

  // Broadcast that the tipping step is done. Each payment flow's
  // `listeningData` reacts to DONE_TIP_TO_STAFF and calls `close()`, which
  // resolves the pending `open()` promise so the flow can continue.
  const handleDone = () => {
    socketContext.send(PAYMENT_PASS_DATA_TOPIC, {
      billId: billIdRef.current,
      actor: TypePaymentActors.CUSTOMER,
      action: TypePaymentPassData.DONE_TIP_TO_STAFF,
      data: { billId: billIdRef.current },
    });
    finish?.();
  };

  if (!open) return null;

  return (
    <ModalStyled
      visible
      hiddenHeader
      hiddenFooter
      noneBodyStyle
      noneFooterStyle
      width={'100vw'}
      footer={<></>}
      destroyOnClose={true}
      onClose={handleDone}
    >
      <Container>
        <Header>
          <Title>Thank you for your PAYMENT!</Title>
          <SkipButton type='button' onClick={handleDone}>
            OK
          </SkipButton>
        </Header>

        <Content>
          <StaffRow>
            {staffs.map((staff) => (
              <StaffCard key={staff.staffId}>
                <StaffInfo>
                  <Avatar>
                    {staff.avatar ? (
                      <img src={staff.avatar} alt={staff.staffName} />
                    ) : (
                      <AvatarFallback>
                        {(staff.staffName || '?').charAt(0).toUpperCase()}
                      </AvatarFallback>
                    )}
                  </Avatar>
                  <StaffName>{staff.staffName}</StaffName>
                </StaffInfo>

                <QrBox>
                  {staff.urlQrVenmoTip ? (
                    <QRCode
                      value={staff.urlQrVenmoTip}
                      size={QR_SIZE}
                      type='svg'
                      bordered={false}
                    />
                  ) : (
                    <QrEmpty>QR not available</QrEmpty>
                  )}
                </QrBox>
              </StaffCard>
            ))}
          </StaffRow>

          <ScanHint>
            <img src={scanQrIcon} alt='' style={{
              width: '42.238px',
              height: '56.068px',
              aspectRatio: '42.24/56.07',
              objectFit: 'cover',
              zoom: '2'
            }} />
            <span>Scan QR to tip</span>
          </ScanHint>
        </Content>
      </Container>
    </ModalStyled>
  );
});
TipToStaff.displayName = 'TipToStaff';
export default TipToStaff;

/* Force the modal to cover the whole viewport (no centered card / chrome). */
const ModalStyled = styled(Modal)`
  &.ant-modal {
    top: 0;
    margin: 0;
    padding: 0;
    max-width: 100vw;
    width: 100vw !important;
    height: 100vh;
  }

  &.ant-modal .ant-modal-content {
    height: 100vh;
    border-radius: 0;
    box-shadow: none;
    background: #fff;
  }

  &.ant-modal .ant-modal-content .ant-modal-body {
    height: 100vh;
    max-height: 100vh !important;
    overflow-x: hidden;
    overflow-y: auto;
  }
`;

/**
 * The whole screen is laid out against the 1440px-wide Figma frame and scaled
 * to the viewport: `--u` is "one design pixel". Every size below is written as
 * `calc(<figmaPx> * var(--u))` so the content scales proportionally — matching
 * the design on any screen width instead of staying a fixed-size block.
 */
const Container = styled.div`
  --u: calc(100vw / 1440);

  display: flex;
  flex-direction: column;
  align-items: center;
  gap: calc(32 * var(--u));
  width: 100%;
  min-height: 100vh;
  padding: calc(24 * var(--u));
  box-sizing: border-box;
`;

const Header = styled.div`
  position: relative;
  display: flex;
  align-items: flex-start;
  justify-content: flex-end;
  width: 100%;
`;

const Title = styled.h1`
  position: absolute;
  left: 50%;
  top: 0;
  transform: translateX(-50%);
  margin: 0;
  width: calc(652 * var(--u));
  max-width: 100%;
  font-family: 'Poppins', sans-serif;
  font-size: calc(24 * var(--u));
  font-weight: 600;
  line-height: normal;
  text-align: center;
  text-transform: uppercase;
  color: #2d6772;
`;

const SkipButton = styled.button`
  flex-shrink: 0;
  border: none;
  border-radius: calc(4 * var(--u));
  padding: calc(12 * var(--u)) calc(24 * var(--u));
  background: #ff8890;
  color: #ffffff;
  font-family: 'Poppins', sans-serif;
  font-size: calc(16 * var(--u));
  font-weight: 600;
  line-height: normal;
  cursor: pointer;
  box-shadow:
    inset 0 calc(-4 * var(--u)) 0 0 #e7646d,
    0 calc(2 * var(--u)) calc(4 * var(--u)) 0 rgba(44, 35, 66, 0.4),
    0 calc(4 * var(--u)) calc(8 * var(--u)) calc(-3 * var(--u)) rgba(45, 35, 66, 0.3);

  &:active {
    box-shadow:
      inset 0 calc(-1 * var(--u)) 0 0 #e7646d,
      0 calc(1 * var(--u)) calc(2 * var(--u)) 0 rgba(44, 35, 66, 0.4);
    transform: translateY(calc(2 * var(--u)));
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: calc(24 * var(--u));
  width: 100%;
`;

const StaffRow = styled.div`
  display: flex;
  align-items: flex-start;
  width: 100%;
`;

const StaffCard = styled.div`
  flex: 1 0 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: calc(24 * var(--u));
  padding: calc(16 * var(--u)) calc(40 * var(--u));
  overflow: hidden;

  &:not(:last-child) {
    border-right: 1px solid #ccd4dc;
  }
`;

const StaffInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: calc(8 * var(--u));
  width: 100%;
`;

const Avatar = styled.div`
  width: calc(100 * var(--u));
  height: calc(100 * var(--u));
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

const AvatarFallback = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #2d6772;
  color: #ffffff;
  font-family: 'Poppins', sans-serif;
  font-size: calc(80 * var(--u));
  font-weight: 600;
`;

const StaffName = styled.div`
  font-family: 'Poppins', sans-serif;
  font-size: calc(24 * var(--u));
  font-weight: 600;
  line-height: normal;
  color: #1d2129;
  text-align: center;
  word-break: break-word;
`;

const QrBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  .ant-qrcode {
    width: calc(276 * var(--u)) !important;
    height: calc(276 * var(--u)) !important;
    padding: 0;
    border: none;
  }

  .ant-qrcode > canvas,
  .ant-qrcode > svg {
    width: 100% !important;
    height: 100% !important;
  }
`;

const QrEmpty = styled.div`
  width: calc(276 * var(--u));
  height: calc(276 * var(--u));
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px dashed #ccd4dc;
  border-radius: calc(12 * var(--u));
  font-family: 'Poppins', sans-serif;
  font-size: calc(18 * var(--u));
  color: #86909c;
  text-align: center;
`;

const ScanHint = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: calc(10 * var(--u));
  width: 100%;

  img {
    width: auto;
    height: calc(56 * var(--u));
    display: block;
  }

  span {
    font-family: 'Poppins', sans-serif;
    font-size: calc(24 * var(--u));
    font-weight: 500;
    line-height: normal;
    color: #1d2129;
  }
`;
